import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { AppSettings } from '../../app.settings';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { UserModel } from '../../user/login.model';
import { AuthService } from '../../api/auth.service';
import { IPService } from '../../api/ip.service';
import countryRegionData from 'country-region-data/data';

const US_CA = {
  CA: [
    'AB',
    'BC',
    'MB',
    'NB',
    'NL',
    'NS',
    'NT',
    'NU',
    'ON',
    'PE',
    'QC',
    'SK',
    'YT'
  ].map(alpha2 => ({ text: alpha2, val: alpha2 })),
  US: [
    'AK',
    'AL',
    'AR',
    'AZ',
    'CA',
    'CO',
    'CT',
    'DC',
    'DE',
    'FL',
    'GA',
    'GU',
    'HI',
    'IA',
    'ID',
    'IL',
    'IN',
    'KS',
    'KY',
    'LA',
    'MA',
    'MD',
    'ME',
    'MH',
    'MI',
    'MN',
    'MO',
    'MS',
    'MT',
    'NC',
    'ND',
    'NE',
    'NH',
    'NJ',
    'NM',
    'NV',
    'NY',
    'OH',
    'OK',
    'OR',
    'PA',
    'PR',
    'PW',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VA',
    'VI',
    'VT',
    'WA',
    'WI',
    'WV',
    'WY',
    'AE',
    'AP',
    'AA'
  ].map(alpha2 => ({ text: alpha2, val: alpha2 }))
};

const ALL_COUNTRIES = countryRegionData.map(country => {
  const states = country.regions.map(region => ({ text: region.name, val: region.shortCode}));
  return {[country.countryShortCode]: states};
});
const STATE_CHOICES = Object.assign({}, ...ALL_COUNTRIES, US_CA);

@Component({
  selector: 'address-block',
  template: `
    <div class="collapsed-form">
      <div class="form-group row">
        <select-field
          class="col-12 col-sm"
          label="Salutation"
          autocomplete="honorific-prefix"
          *ngIf="showTitle"
          [items]="titles"
          [required]="requireTitle"
          [ngModel]="addressSource.title"
          (ngModelChange)="emitFieldValue('title', $event)"
        >
        </select-field>
        <text-field
          class="col-12 col-sm"
          label="First Name"
          autocomplete="given-name"
          [ngModel]="addressSource.firstName"
          [showValidation]="showValidation"
          (ngModelChange)="emitFieldValue('firstName', $event)"
          [maxLength]="100"
          [required]="!optional"
        >
        </text-field>
        <text-field
          class="col-12 col-sm"
          label="Middle Name"
          autocomplete="additional-name"
          [ngModel]="addressSource.middleName"
          *ngIf="showMiddle"
          [showValidation]="showValidation"
          (ngModelChange)="emitFieldValue('middleName', $event)"
        >
        </text-field>
        <text-field
          class="col-12 col-sm"
          label="Last Name"
          autocomplete="family-name"
          [ngModel]="addressSource.lastName"
          [showValidation]="showValidation"
          (ngModelChange)="emitFieldValue('lastName', $event)"
          [required]="!optional"
          [maxLength]="100"
        >
        </text-field>
      </div>
      <div class="form-group row" *ngIf="showCompany">
        <text-field
          class="col-12"
          label="Company"
          type="text"
          autocomplete="organization"
          [ngModel]="addressSource.company"
          [showValidation]="showValidation"
          (ngModelChange)="emitFieldValue('company', $event)"
        >
        </text-field>
      </div>
      <div class="form-group row">
        <text-field
          class="col-12 col-sm"
          label="Email"
          autocomplete="email"
          type="email"
          [ngModel]="addressSource.email"
          [showValidation]="showValidation"
          [required]="requireEmail"
          (ngModelChange)="emitFieldValue('email', $event)"
        >
        </text-field>
        <phone-field
          class="col-12 col-sm"
          label="Phone Number"
          autocomplete="tel"
          [ngModel]="addressSource.phone"
          [country]="addressSource.country"
          [showValidation]="showValidation"
          [required]="requirePhone"
          (countryChange)="onPhoneInputCountryChange($event)"
          (ngModelChange)="emitFieldValue('phone', $event)"
        >
        </phone-field>
      </div>
      <div class="form-group row" *ngIf="international">
        <select-field
          class="col-12"
          label="Country"
          autocomplete="country"
          [items]="countries"
          [ngModel]="addressSource.country"
          [showValidation]="showValidation"
          [required]="!optional"
          (ngModelChange)="emitFieldValue('country', $event)"
        >
        </select-field>
      </div>
      <div class="form-group row">
        <address-field
          class="col-12"
          label="Address 1"
          autocomplete="address-line1"
          type="address"
          [ngModel]="addressSource.address1"
          [showValidation]="showValidation"
          [required]="!optional"
          [maxLength]="256"
          (onAddressChange)="receiveGooglePlaceAddress($event)"
          (ngModelChange)="emitFieldValue('address1', $event)"
        >
        </address-field>
      </div>
      <div class="form-group row">
        <text-field
          class="col-12"
          label="Address 2"
          autocomplete="address-line2"
          [ngModel]="addressSource.address2"
          [showValidation]="showValidation"
          [maxLength]="256"
          (ngModelChange)="emitFieldValue('address2', $event)"
        >
        </text-field>
      </div>
      <div class="form-group row">
        <text-field
          class="col-12 col-sm-6"
          label="City"
          autocomplete="address-level2"
          [ngModel]="addressSource.city"
          [showValidation]="showValidation"
          [required]="!optional"
          [maxLength]="100"
          (ngModelChange)="emitFieldValue('city', $event)"
        >
        </text-field>
        <select-field
          class="col-6 col-sm"
          autocomplete="address-level1"
          [label]="addressSource.country === 'US' ? 'State' : 'Province'"
          [items]="stateOptions[addressSource.country || 'US']"
          [ngModel]="addressSource.state"
          [showValidation]="showValidation"
          [required]="!optional"
          (ngModelChange)="emitFieldValue('state', $event)"
        >
        </select-field>
        <zip-field
          class="col-6 col-sm"
          label="Zip"
          autocomplete="postal-code"
          [ngModel]="addressSource.zip"
          [country]="addressSource.country"
          [showValidation]="showValidation"
          [required]="!optional"
          (ngModelChange)="emitFieldValue('zip', $event)"
        >
        </zip-field>
        <!--
          hidden inputs are for receiving autofill data from the browser
          for ng-select, which is not compatible w/ autocomplete attributes...
        -->
      </div>
      <input
        type="text"
        tabindex="-1000"
        style="height: 0;border: 0;margin: 0;padding: 0; display: block; position: absolute;"
        [ngModel]="addressSource.state"
        (ngModelChange)="emitFieldValue('state', $event)"
        autocomplete="address-level1"
      />
      <input
        type="text"
        tabindex="-1000"
        style="height: 0;border: 0;margin: 0;padding: 0; display: block; position: absolute;"
        (ngModelChange)="emitFieldValue('country', $event)"
        [ngModel]="addressSource.country"
        autocomplete="country"
      />
    </div>
  `
})
export class AddressBlockComponent implements OnInit {
  @Input() showMiddle = false;
  @Input() showCompany = false;
  @Input() showValidation = false;
  @Input() optional = false;
  @Input() showTitle = false;
  @Input() requireTitle = false;
  @Input() requirePhone = false;
  @Input() requireEmail = false;
  @Input() titles = [];
  countries;
  @Input() international;
  @Input() addressSource;

  // this was never a proper blur event, and is even less so now... consider renaming
  @Output() blur = new EventEmitter<any>();
  @Select() user$: Observable<UserModel>;

  stateOptions = STATE_CHOICES;

  constructor(
    private auth: AuthService,
    private settings: AppSettings,
    private ipService: IPService
  ) {}

  ngOnInit() {
    if (this.international) {
      this.countries = this.settings
        .getValue('organization')
        .supported_countries.map(item => ({ text: item.text, val: item.id }));
    }

    // attempt to lookup user's current country via geoIP service if it hasn't
    // already been populated in the login component
    if (!this.addressSource.country) {
      this.ipService.getIP().subscribe((data: any) => {
        // directly emit country if set during init
        this.blur.emit({ name: 'country', value: data.countryCode });
      });
    }
  }

  emitFieldValue(controlName, newValue) {
    // TODO : move this trackAction call somewhere useful but not spammy
    // should be bound to actual blur events (user entering and then leaving the field),

    if (controlName === 'country') {
      if (newValue !== this.addressSource.country) {
        // reset state when country changes
        this.blur.emit({ name: 'state', value: null });
      }

      // retain the special-case where we truncate zip to 5 digits when changing to US
      if (
        newValue === 'US' &&
        this.addressSource.zip &&
        this.addressSource.zip.length > 5
      ) {
        this.blur.emit({
          name: 'zip',
          value: this.addressSource.zip.substr(0, 5)
        });
      }
    } else if (controlName === 'zip') {
      // also truncate zip if it has changed and country is appropriate
      if (
        this.addressSource.country === 'US' &&
        newValue &&
        newValue.length > 5
      ) {
        newValue = newValue.substr(0, 5);
      }
    }

    this.blur.emit({ name: controlName, value: newValue });
  }

  onPhoneInputCountryChange($event) {
    // user changed country, via phone input
    this.emitFieldValue('country', $event.iso2.toUpperCase());
  }

  receiveGooglePlaceAddress(result) {
    this.emitFieldValue('address1', result.name);

    result.address_components.map(item => {
      if (item.types[0]) {
        switch (item.types[0]) {
          case 'administrative_area_level_1': {
            this.emitFieldValue('state', item.short_name);
            break;
          }
          case 'locality': {
            this.emitFieldValue('city', item.short_name);
            break;
          }
          case 'postal_code': {
            this.emitFieldValue('zip', item.short_name);
            break;
          }
        }
      }
    });
  }
}
